/**
 * AiContentAuditDialog.jsx
 *
 * Opens from Setup → AI Content Audit.
 *
 * Tab 1 – AI Audit    : opt-in tracking, traffic-light, chapter breakdown,
 *                        snapshot history, creative activity summary,
 *                        copyright filing service.
 * Tab 2 – Evidence Vault: upload timestamped original ideas (text + optional
 *                        file) for copyright litigation evidence.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, Camera, ChevronDown, ChevronUp, Info,
  FolderLock, Plus, Trash2, Download, FileText, X, Upload,
  Pen, BadgeCheck, Archive,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { AiLoadingOverlay } from '../ui/AiLoadingOverlay';
import { copyrightFilingApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── Shared constants ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'audit',    label: 'AI Audit',       Icon: ShieldCheck },
  { id: 'evidence', label: 'Evidence Vault',  Icon: FolderLock  },
];

const EVIDENCE_CATEGORIES = [
  'Plot Idea', 'Character', 'Setting', 'Dialogue', 'Research', 'Other',
];

const CATEGORY_COLORS = {
  'Plot Idea':  'bg-violet-500/20 text-violet-300 border-violet-500/30',
  'Character':  'bg-sky-500/20    text-sky-300    border-sky-500/30',
  'Setting':    'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Dialogue':   'bg-amber-500/20  text-amber-300  border-amber-500/30',
  'Research':   'bg-rose-500/20   text-rose-300   border-rose-500/30',
  'Other':      'bg-neutral-500/20 text-neutral-300 border-neutral-500/30',
};

const ALLOWED_EXTS = '.pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.webp';

// ── Traffic-light config ─────────────────────────────────────────────────────
const LIGHT = {
  green:  { Icon: CheckCircle2, ring: 'ring-emerald-500/60', glow: 'bg-emerald-500/15', dot: 'bg-emerald-400', text: 'text-emerald-300',  label: 'Safe' },
  yellow: { Icon: AlertTriangle, ring: 'ring-amber-400/60',  glow: 'bg-amber-400/10',   dot: 'bg-amber-400',   text: 'text-amber-300',    label: 'Marginal' },
  red:    { Icon: XCircle,       ring: 'ring-rose-500/60',   glow: 'bg-rose-500/10',    dot: 'bg-rose-400',    text: 'text-rose-300',     label: 'At Risk' },
};

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(n) { return (n || 0).toLocaleString(); }
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16).replace('T', ' '); }
}
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Percentage gauge bar ─────────────────────────────────────────────────────
function GaugeBar({ aiPct }) {
  const pct = Math.min(100, Math.max(0, aiPct));
  const color = pct < 25 ? 'bg-emerald-500' : pct <= 40 ? 'bg-amber-400' : 'bg-rose-500';
  return (
    <div className="space-y-1">
      <div className="relative h-3 w-full rounded-full bg-neutral-800 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-700', color)}
          style={{ width: `${pct}%` }}
        />
        <div className="absolute top-0 left-[25%] h-full w-px bg-amber-400/50" title="25% — yellow zone" />
        <div className="absolute top-0 left-[40%] h-full w-px bg-rose-500/50" title="40% — red zone" />
      </div>
      <div className="flex text-[9.5px] text-neutral-600">
        <span>0%</span>
        <span className="absolute left-[25%] -translate-x-1/2 text-amber-500/70">25%</span>
        <span className="absolute left-[40%] -translate-x-1/2 text-rose-500/70">40%</span>
        <span className="ml-auto">100%</span>
      </div>
    </div>
  );
}

// ── Chapter row ──────────────────────────────────────────────────────────────
function ChapterRow({ ch }) {
  const status = ch.ai_pct < 25 ? 'green' : ch.ai_pct <= 40 ? 'yellow' : 'red';
  const { dot } = LIGHT[status];
  return (
    <tr className="border-b border-neutral-800/50 text-[11.5px]">
      <td className="py-1.5 pr-4 text-neutral-300 max-w-[180px] truncate">
        <span className={cn('inline-block w-2 h-2 rounded-full mr-2 shrink-0', dot)} />
        {ch.title}
      </td>
      <td className="py-1.5 pr-3 text-right text-neutral-400">{fmt(ch.words)}</td>
      <td className="py-1.5 pr-3 text-right text-neutral-400">{fmt(ch.ai_words)}</td>
      <td className={cn('py-1.5 text-right font-medium', LIGHT[status].text)}>{ch.ai_pct.toFixed(1)}%</td>
    </tr>
  );
}

// ── Evidence form ────────────────────────────────────────────────────────────
function EvidenceForm({ projectId, onAdded }) {
  const [title,    setTitle]    = useState('');
  const [category, setCategory] = useState('Plot Idea');
  const [body,     setBody]     = useState('');
  const [file,     setFile]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!title.trim()) { toast.error('A title is required.'); return; }
    if (!body.trim() && !file) { toast.error('Add a text entry, a file, or both.'); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title',    title.trim());
      fd.append('category', category);
      fd.append('body',     body.trim());
      if (file) fd.append('file', file);

      const r = await fetch(`${API}/projects/${projectId}/copyright-evidence`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Submission failed');
      const { evidence } = await r.json();
      toast.success('Evidence entry saved and timestamped');
      setTitle(''); setBody(''); clearFile();
      onAdded(evidence);
    } catch (e) {
      toast.error(e.message || 'Failed to save evidence');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-700/50 bg-neutral-800/30 p-4 space-y-3">
      <p className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wide">
        Add new evidence entry
      </p>

      {/* Title */}
      <input
        type="text"
        placeholder="Entry title (e.g. 'Original opening for Act 2')"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        className="w-full h-8 rounded border border-neutral-700 bg-neutral-800 px-3 text-[12px] text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
        data-testid="evidence-title-input"
      />

      {/* Category pills */}
      <div className="flex flex-wrap gap-1.5">
        {EVIDENCE_CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategory(cat)}
            className={cn(
              'px-2.5 py-0.5 rounded-full border text-[11px] font-medium transition-colors',
              category === cat
                ? CATEGORY_COLORS[cat]
                : 'border-neutral-700 text-neutral-500 hover:text-neutral-300',
            )}
            data-testid={`evidence-cat-${cat.toLowerCase().replace(' ', '-')}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Body textarea */}
      <textarea
        placeholder="Describe your original idea, character concept, plot beat, or paste relevant text…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={50000}
        rows={5}
        className="w-full rounded border border-neutral-700 bg-neutral-800 px-3 py-2 text-[12px] text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-amber-500/50 resize-y"
        data-testid="evidence-body-input"
      />

      {/* File upload */}
      <div>
        {file ? (
          <div className="flex items-center gap-2 rounded border border-neutral-700 bg-neutral-800/60 px-3 py-2">
            <FileText className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span className="text-[11.5px] text-neutral-300 flex-1 min-w-0 truncate">{file.name}</span>
            <span className="text-[10.5px] text-neutral-500">{fmtSize(file.size)}</span>
            <button type="button" onClick={clearFile} className="ml-1 text-neutral-500 hover:text-rose-400">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 text-[11.5px] text-neutral-500 hover:text-neutral-300 transition-colors"
            data-testid="evidence-file-btn"
          >
            <Upload className="h-3.5 w-3.5" />
            Attach a file (PDF, DOCX, TXT, image — max 20 MB)
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={ALLOWED_EXTS}
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="evidence-file-input"
        />
      </div>

      <Button
        size="sm"
        className="bg-amber-500 hover:bg-amber-400 text-neutral-900 font-semibold h-8"
        onClick={submit}
        disabled={submitting}
        data-testid="evidence-submit-btn"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        {submitting ? 'Saving…' : 'Save evidence entry'}
      </Button>
    </div>
  );
}

// ── Evidence entry card ───────────────────────────────────────────────────────
function EvidenceCard({ item, projectId, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDelete = async () => {
    if (!confirming) { setConfirming(true); return; }
    setDeleting(true);
    try {
      const r = await fetch(
        `${API}/projects/${projectId}/copyright-evidence/${item.evidence_id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!r.ok) throw new Error((await r.json()).detail || 'Delete failed');
      toast.success('Evidence entry removed');
      onDeleted(item.evidence_id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await fetch(
        `${API}/projects/${projectId}/copyright-evidence/${item.evidence_id}/download`,
        { credentials: 'include' },
      );
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = item.file_name || 'evidence';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-lg border border-neutral-700/40 bg-neutral-800/20 p-3.5 space-y-2">
      {/* header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                'inline-block px-2 py-0.5 rounded-full border text-[10.5px] font-medium',
                CATEGORY_COLORS[item.category] || CATEGORY_COLORS['Other'],
              )}
            >
              {item.category}
            </span>
            <p className="text-[13px] font-medium text-neutral-200 truncate">{item.title}</p>
          </div>
          <p className="text-[10.5px] text-neutral-600 mt-0.5">
            Timestamped {fmtDate(item.created_at)}
          </p>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {item.file_name && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-neutral-500 hover:text-amber-300"
              onClick={handleDownload}
              disabled={downloading}
              title={`Download ${item.file_name}`}
              data-testid={`evidence-download-${item.evidence_id}`}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              'h-7 px-2 transition-colors',
              confirming ? 'text-rose-400 hover:text-rose-300' : 'text-neutral-600 hover:text-rose-400',
            )}
            onClick={handleDelete}
            disabled={deleting}
            title={confirming ? 'Click again to confirm deletion' : 'Delete entry'}
            data-testid={`evidence-delete-${item.evidence_id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {confirming && (
            <button
              type="button"
              className="text-[10.5px] text-neutral-500 hover:text-neutral-300 ml-0.5"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* body excerpt */}
      {item.body && (
        <p className="text-[11.5px] text-neutral-400 leading-relaxed line-clamp-3 whitespace-pre-wrap">
          {item.body}
        </p>
      )}

      {/* attached file badge */}
      {item.file_name && (
        <div className="flex items-center gap-1.5 text-[10.5px] text-neutral-500">
          <FileText className="h-3 w-3" />
          <span>{item.file_name}</span>
          {item.file_size && <span className="text-neutral-700">· {fmtSize(item.file_size)}</span>}
        </div>
      )}
    </div>
  );
}

// ── Evidence Vault tab ────────────────────────────────────────────────────────
function EvidenceVaultTab({ projectId }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/projects/${projectId}/copyright-evidence`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to load');
      const { items: list } = await r.json();
      setItems(list || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load evidence entries');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleAdded = (entry) => setItems((prev) => [entry, ...prev]);
  const handleDeleted = (eid) => setItems((prev) => prev.filter((x) => x.evidence_id !== eid));

  return (
    <div className="relative space-y-4">
      <AiLoadingOverlay visible={loading} message="Loading evidence vault…" blur={false} />
      {/* intro callout */}
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3.5 space-y-1.5">
        <div className="flex items-center gap-2">
          <FolderLock className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="text-[12.5px] font-semibold text-amber-300">Copyright Evidence Vault</p>
        </div>
        <p className="text-[11.5px] text-neutral-400 leading-relaxed">
          Submit your original ideas, character sketches, plot notes, and research snippets
          with an <strong className="text-neutral-300">immutable server-side timestamp</strong>.
          These records can demonstrate prior creative authorship in copyright disputes
          or infringement litigation.
        </p>
        <p className="text-[10.5px] text-neutral-600">
          Entries are stored securely and never modified after submission.
          Attach supporting files (scanned handwritten notes, early drafts, reference docs).
        </p>
      </div>

      {/* submission form */}
      <EvidenceForm projectId={projectId} onAdded={handleAdded} />

      {/* entries list */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-semibold text-neutral-400 uppercase tracking-wide">
            Vault entries ({items.length})
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-neutral-600 hover:text-neutral-300"
            onClick={load}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </Button>
        </div>

        {loading && items.length === 0 && (
          <div className="flex items-center justify-center py-8 text-neutral-600 text-[12px]">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            Loading vault…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-neutral-600 space-y-2">
            <FolderLock className="h-8 w-8 opacity-30" />
            <p className="text-[12px]">No evidence entries yet.</p>
            <p className="text-[11px] text-neutral-700">
              Add your first entry above to start building your evidence trail.
            </p>
          </div>
        )}

        {items.map((item) => (
          <EvidenceCard
            key={item.evidence_id}
            item={item}
            projectId={projectId}
            onDeleted={handleDeleted}
          />
        ))}
      </div>
    </div>
  );
}

// ── Creative Activity summary badge ──────────────────────────────────────────
function CreativeActivityBadge({ projectId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    fetch(`${API}/projects/${projectId}/creative-interactions`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setData(d))
      .catch(() => {});
  }, [projectId]);

  if (!data) return null;
  const total = data.total || 0;
  if (total === 0) return null;

  return (
    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 flex items-start gap-3">
      <Pen className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-semibold text-emerald-300">
          {total.toLocaleString()} creative interaction{total !== 1 ? 's' : ''} recorded
          <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/30">
            <BadgeCheck className="h-3 w-3" />
            High copyright confidence
          </span>
        </p>
        <p className="text-[11px] text-neutral-500 mt-0.5">
          Every direct paragraph edit, merge, and chapter structural change is timestamped
          as evidence of human creative authorship.
        </p>
        {(data.by_chapter_summary || []).slice(0, 3).map((ch) => (
          <span
            key={ch.chapter_id}
            className="inline-block mr-1.5 mt-1.5 text-[10.5px] text-neutral-500 bg-neutral-800/50 px-2 py-0.5 rounded border border-neutral-700/40"
          >
            {ch.chapter_title || 'Untitled'} · {ch.count}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Copyright Filing section ──────────────────────────────────────────────────
const FILING_DISPLAY = {
  single:   { color: 'bg-sky-500/15 border-sky-500/30 text-sky-300',        badge: 'Most popular' },
  standard: { color: 'bg-amber-500/15 border-amber-500/30 text-amber-300',  badge: 'Most complete' },
  paper:    { color: 'bg-neutral-500/15 border-neutral-700/50 text-neutral-400', badge: 'Physical cert' },
};

function CopyrightFilingSection({ projectId }) {
  const [options,   setOptions]   = useState([]);
  const [filings,   setFilings]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [paying,    setPaying]    = useState(null);  // filing_type currently being checked out

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [pricesRes, statusRes] = await Promise.all([
        copyrightFilingApi.prices(),
        copyrightFilingApi.status(projectId),
      ]);
      setOptions(pricesRes.options || []);
      setFilings(statusRes.filings || []);
    } catch {
      // non-fatal — section just stays empty
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const startCheckout = async (filingType) => {
    setPaying(filingType);
    try {
      const origin = window.location.origin;
      const { url } = await copyrightFilingApi.checkout(projectId, {
        filing_type: filingType,
        origin_url:  origin,
      });
      window.location.href = url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Checkout failed';
      toast.error(msg);
    } finally {
      setPaying(null);
    }
  };

  const paidTypes = new Set(filings.filter((f) => f.payment_status === 'paid').map((f) => f.filing_type));
  const pendingTypes = new Set(filings.filter((f) => f.payment_status === 'pending').map((f) => f.filing_type));

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BadgeCheck className="h-4 w-4 text-amber-400 shrink-0" />
        <p className="text-[13px] font-semibold text-neutral-200">
          File with the U.S. Copyright Office
        </p>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-6 px-1.5 text-neutral-600 hover:text-neutral-300"
          onClick={loadData}
          disabled={loading}
          title="Refresh"
        >
          <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
        </Button>
      </div>
      <p className="text-[11.5px] text-neutral-500 leading-relaxed">
        Crown &amp; Rose files your application with the U.S. Copyright Office on your behalf.
        We charge a <strong className="text-neutral-300">$15 service fee</strong> on top of
        the official filing fee. You receive a confirmation email with a reference number;
        your registration certificate arrives from the Copyright Office separately.
      </p>

      {/* Paid filings status */}
      {filings.filter((f) => f.payment_status === 'paid').map((f) => (
        <div key={f.filing_id} className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 flex items-center gap-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium text-emerald-300">
              {f.filing_option?.name || f.filing_type} — Filing submitted
            </p>
            <p className="text-[10.5px] text-neutral-600">
              Ref: {f.reference_number || '—'} · Paid {fmtDate(f.paid_at)}
            </p>
          </div>
        </div>
      ))}

      {/* Price cards */}
      {loading && options.length === 0 && (
        <div className="flex items-center justify-center py-6 text-neutral-600 text-[12px]">
          <RefreshCw className="h-3.5 w-3.5 animate-spin mr-2" />
          Loading prices…
        </div>
      )}
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        {options.map((opt) => {
          const style = FILING_DISPLAY[opt.id] || FILING_DISPLAY.paper;
          const isPaid    = paidTypes.has(opt.id);
          const isPending = pendingTypes.has(opt.id);
          return (
            <div
              key={opt.id}
              className={cn(
                'rounded-xl border p-4 space-y-2.5',
                isPaid ? 'border-emerald-500/30 bg-emerald-500/5' : style.color,
              )}
              data-testid={`filing-card-${opt.id}`}
            >
              {/* Badge */}
              <div className="flex items-center justify-between">
                <span className={cn('text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border', style.color)}>
                  {style.badge}
                </span>
                {isPaid && <BadgeCheck className="h-4 w-4 text-emerald-400" />}
              </div>

              <p className="text-[13px] font-bold text-neutral-200">{opt.name}</p>
              <p className="text-[10.5px] text-neutral-500 leading-relaxed">{opt.description}</p>

              {/* Pricing breakdown */}
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between text-neutral-500">
                  <span>Copyright Office fee</span>
                  <span>${opt.co_fee_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-neutral-500">
                  <span>Service fee</span>
                  <span>${opt.platform_fee_usd.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-neutral-200 border-t border-neutral-700/50 pt-1 mt-1">
                  <span>Total</span>
                  <span>${opt.total_usd.toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[10px] text-neutral-600">
                Processing: {opt.processing_days} business days
              </p>

              {isPaid ? (
                <Button
                  size="sm"
                  className="w-full h-8 bg-emerald-600 text-white cursor-default"
                  disabled
                  data-testid={`filing-paid-${opt.id}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Filed
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="w-full h-8 bg-amber-500 hover:bg-amber-400 text-neutral-900 font-semibold"
                  onClick={() => startCheckout(opt.id)}
                  disabled={paying !== null || isPending}
                  data-testid={`filing-checkout-${opt.id}`}
                >
                  {paying === opt.id ? (
                    <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Redirecting…</>
                  ) : isPending ? (
                    'Payment pending…'
                  ) : (
                    `File for $${opt.total_usd.toFixed(0)}`
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-neutral-700 leading-relaxed">
        Fees current as of 2026 U.S. Copyright Office fee schedule.
        Single Application $55 + $15 service · Standard Application $85 + $15 service ·
        Paper Filing $185 + $15 service. The $15 service fee is non-refundable.
        Copyright Office fees are governed by their policies.
      </p>
    </div>
  );
}

// ── ZIP Download helper ───────────────────────────────────────────────────────
function ZipDownloadButton({ projectId }) {
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/projects/${projectId}/creative-audit/download`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const cd   = r.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'creative_audit.zip';
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit archive downloaded');
    } catch {
      toast.error('Download failed — please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-8 border-neutral-700 text-neutral-400 hover:text-neutral-200"
      onClick={handleDownload}
      disabled={busy}
      data-testid="audit-zip-download-btn"
      title="Download a ZIP of all creative interactions, diffs, audit snapshots and evidence entries"
    >
      <Archive className={cn('h-3.5 w-3.5 mr-1.5', busy && 'animate-bounce')} />
      {busy ? 'Preparing…' : 'Download Audit ZIP'}
    </Button>
  );
}

// ── AI Audit tab (extracted from original component) ─────────────────────────
function AiAuditTab({ projectId }) {  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [toggling,      setToggling]      = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [snapshotNote,  setSnapshotNote]  = useState('');
  const [history,       setHistory]       = useState([]);
  const [showChapters,  setShowChapters]  = useState(false);
  const [showHistory,   setShowHistory]   = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [auditRes, histRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/ai-audit`, { credentials: 'include' }),
        fetch(`${API}/projects/${projectId}/ai-audit/history`, { credentials: 'include' }),
      ]);
      if (!auditRes.ok) throw new Error((await auditRes.json()).detail || 'Failed to load audit');
      const [audit, hist] = await Promise.all([auditRes.json(), histRes.json()]);
      setData(audit);
      setHistory(hist.snapshots || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load AI audit data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [load, projectId]);

  const toggleOptIn = async (enabled) => {
    setToggling(true);
    try {
      const r = await fetch(`${API}/projects/${projectId}/ai-audit/opt-in`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to update');
      setData((d) => ({ ...d, opt_in: enabled }));
      toast.success(enabled ? 'Audit tracking enabled' : 'Audit tracking paused');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setToggling(false);
    }
  };

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/projects/${projectId}/ai-audit/snapshot`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: snapshotNote }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to save');
      const { snapshot } = await r.json();
      setHistory((h) => [snapshot, ...h]);
      setSnapshotNote('');
      toast.success('Snapshot saved to audit history');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const status = data?.traffic_light?.status || 'green';
  const light  = LIGHT[status];
  const { Icon } = light;
  const aiPct  = data?.ai_pct ?? 0;

  return (
    <div className="relative min-h-[200px]">
      <AiLoadingOverlay
        visible={loading}
        message="Analysing manuscript for AI content…"
      />

      {!loading && data && (
        <>
          {/* ── Legal context ─────────────────────────── */}
          <div className="rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-4 space-y-2">
            <p className="text-[11.5px] font-semibold text-neutral-400 uppercase tracking-wide">
              Why track this?
            </p>
            <ul className="space-y-1.5">
              {[
                { flag: '🇺🇸', ref: 'US Copyright Act, 17 U.S.C. § 102(a)', text: 'The US Copyright Office requires works to contain sufficient human authorship. AI-generated content cannot be copyright-protected on its own (USCO guidance, Jan 2024).' },
                { flag: '🇬🇧', ref: 'UK CDPA 1988, s.9(3)', text: 'Computer-generated works may qualify if a human made the necessary arrangements — but courts weigh creative contribution. Predominantly AI works face uncertain protection.' },
                { flag: '📦', ref: 'Amazon KDP — AI Content Policy (2023)', text: 'Authors must disclose AI-generated text, images, and translations at publishing. Undisclosed AI content risks de-listing.' },
              ].map(({ flag, ref, text }) => (
                <li key={ref} className="flex gap-2 text-[11.5px] text-neutral-400">
                  <span className="shrink-0 text-base leading-none">{flag}</span>
                  <span><strong className="text-neutral-300">{ref}</strong> — {text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Opt-in toggle ─────────────────────────── */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="audit-optin" className="text-[13px] font-medium text-neutral-200">
                Enable audit tracking for this work
              </Label>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                {data.opt_in
                  ? `Active since ${fmtDate(data.opted_in_at)}`
                  : 'Off — turn on to enable snapshot history'}
              </p>
            </div>
            <Switch
              id="audit-optin"
              checked={data.opt_in}
              onCheckedChange={toggleOptIn}
              disabled={toggling}
              data-testid="ai-audit-optin-toggle"
            />
          </div>

          {/* ── Traffic-light + metrics ───────────────── */}
          <div className={cn('rounded-xl border p-5 space-y-4', light.ring, light.glow, 'ring-1')}>
            <div className="flex items-start gap-4">
              <div className={cn('rounded-full p-2.5', light.glow)}>
                <Icon className={cn('h-7 w-7', light.text)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn('text-lg font-bold', light.text)} data-testid="ai-audit-status-label">
                  {light.label} — {aiPct.toFixed(1)}% AI content
                </p>
                <p className="text-[12px] text-neutral-400 mt-0.5 leading-snug">
                  {data.traffic_light.detail}
                </p>
              </div>
            </div>

            <GaugeBar aiPct={aiPct} />

            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Total words',  value: fmt(data.total_words),  sub: '100%' },
                { label: 'Human words',  value: fmt(data.human_words),  sub: `${(100 - aiPct).toFixed(1)}%`, highlight: 'text-emerald-400' },
                { label: 'AI words',     value: fmt(data.ai_words),     sub: `${aiPct.toFixed(1)}%`,         highlight: light.text },
              ].map(({ label, value, sub, highlight }) => (
                <div key={label} className="rounded-lg border border-neutral-700/40 bg-neutral-800/40 p-3 text-center">
                  <p className={cn('text-xl font-bold', highlight || 'text-neutral-100')}>{value}</p>
                  <p className="text-[11px] text-neutral-500">{label}</p>
                  <p className={cn('text-[11px] font-medium mt-0.5', highlight || 'text-neutral-400')}>{sub}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-4 text-[11px] text-neutral-500">
              <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" />Green &lt;25%</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />Yellow 25–40%</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-rose-400 mr-1" />Red &gt;40%</span>
            </div>
          </div>

          {/* ── Chapter breakdown ─────────────────────── */}
          {data.chapters && data.chapters.length > 0 && (
            <div className="rounded-lg border border-neutral-700/40 overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-[12.5px] font-medium text-neutral-300 hover:bg-neutral-800/40 transition-colors"
                onClick={() => setShowChapters((v) => !v)}
                data-testid="ai-audit-chapters-toggle"
              >
                <span>Chapter breakdown ({data.chapters.length} chapters)</span>
                {showChapters ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {showChapters && (
                <div className="px-4 pb-3 overflow-x-auto">
                  <table className="w-full min-w-[400px]">
                    <thead>
                      <tr className="text-[10.5px] uppercase tracking-wide text-neutral-600 border-b border-neutral-800">
                        <th className="py-1.5 text-left pr-4">Chapter</th>
                        <th className="py-1.5 text-right pr-3">Words</th>
                        <th className="py-1.5 text-right pr-3">AI</th>
                        <th className="py-1.5 text-right">AI %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.chapters.map((ch) => (
                        <ChapterRow key={ch.chapter_id || ch.title} ch={ch} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Snapshot / history ────────────────────── */}
          {data.opt_in && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Optional note for this snapshot (e.g. 'Before final edit pass')"
                  value={snapshotNote}
                  onChange={(e) => setSnapshotNote(e.target.value)}
                  className="flex-1 h-8 rounded border border-neutral-700 bg-neutral-800 px-3 text-[12px] text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                  data-testid="ai-audit-snapshot-note"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  onClick={saveSnapshot}
                  disabled={saving}
                  data-testid="ai-audit-save-snapshot-btn"
                >
                  <Camera className="h-3.5 w-3.5 mr-1.5" />
                  {saving ? 'Saving…' : 'Save snapshot'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-neutral-500 hover:text-neutral-300"
                  onClick={load}
                  title="Refresh"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>

              {history.length > 0 && (
                <div className="rounded-lg border border-neutral-700/40 overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-[12.5px] font-medium text-neutral-300 hover:bg-neutral-800/40 transition-colors"
                    onClick={() => setShowHistory((v) => !v)}
                    data-testid="ai-audit-history-toggle"
                  >
                    <span>Audit history ({history.length} snapshots)</span>
                    {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  {showHistory && (
                    <table className="w-full text-[11.5px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-neutral-600 border-b border-neutral-800">
                          <th className="py-1.5 px-4 text-left">Date</th>
                          <th className="py-1.5 px-3 text-right">AI%</th>
                          <th className="py-1.5 px-3 text-right">Words</th>
                          <th className="py-1.5 px-4 text-left">Note</th>
                          <th className="py-1.5 px-4 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((s) => {
                          const sl = LIGHT[s.traffic_light_status || 'green'];
                          return (
                            <tr key={s.snapshot_id} className="border-b border-neutral-800/50">
                              <td className="py-1.5 px-4 text-neutral-400">{fmtDate(s.saved_at)}</td>
                              <td className={cn('py-1.5 px-3 text-right font-medium', sl.text)}>{s.ai_pct?.toFixed(1)}%</td>
                              <td className="py-1.5 px-3 text-right text-neutral-500">{fmt(s.total_words)}</td>
                              <td className="py-1.5 px-4 text-neutral-500 max-w-[180px] truncate">{s.note || '—'}</td>
                              <td className="py-1.5 px-4 text-center">
                                <span className={cn('inline-block w-2 h-2 rounded-full', sl.dot)} title={sl.label} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {!data.opt_in && (
            <div className="flex gap-2.5 rounded-lg border border-neutral-700/40 bg-neutral-800/20 p-3.5">
              <Info className="h-4 w-4 text-neutral-500 shrink-0 mt-0.5" />
              <p className="text-[11.5px] text-neutral-500">
                Enable audit tracking above to save timestamped snapshots of this
                work's AI content ratio — useful for publisher submissions, copyright
                registration, or Amazon KDP disclosure records.
              </p>
            </div>
          )}

          {/* Creative Activity */}
          <div className="border-t border-neutral-800/50 pt-4">
            <CreativeActivityBadge projectId={projectId} />
          </div>

          {/* Copyright Filing */}
          <div className="border-t border-neutral-800/50 pt-4">
            <CopyrightFilingSection projectId={projectId} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Main dialog ──────────────────────────────────────────────────────────────
export default function AiContentAuditDialog({ open, onOpenChange, projectId }) {
  const [activeTab, setActiveTab] = useState('audit');

  // Reset to first tab when dialog opens
  useEffect(() => {
    if (open) setActiveTab('audit');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] bg-[#141312] border-neutral-800 text-neutral-100 p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-0 border-b border-neutral-800">
          <div className="flex items-center gap-2.5 mb-3">
            <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0" />
            <DialogTitle className="text-base font-semibold text-neutral-100">
              AI Content Audit &amp; Copyright Evidence
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Track AI vs human content ratios and build a timestamped evidence vault for copyright protection.
          </DialogDescription>

          {/* Tab bar */}
          <div className="flex gap-0">
            {TABS.map(({ id, label, Icon: TabIcon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 transition-colors',
                  activeTab === id
                    ? 'border-amber-400 text-amber-300'
                    : 'border-transparent text-neutral-500 hover:text-neutral-300',
                )}
                data-testid={`audit-tab-${id}`}
              >
                <TabIcon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className="flex-1 px-6 py-5 space-y-5 overflow-y-auto min-h-0">
          {activeTab === 'audit' && <AiAuditTab projectId={projectId} />}
          {activeTab === 'evidence' && <EvidenceVaultTab projectId={projectId} />}
        </div>

        <div className="px-6 py-3 border-t border-neutral-800 flex items-center justify-between">
          <ZipDownloadButton projectId={projectId} />
          <Button
            size="sm"
            variant="ghost"
            className="text-neutral-500"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
